import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { FikoYTService } from '../../../../../service/fikoyt.service';

export type WsBaglantiDurum = 'bagli-degil' | 'baglaniyor' | 'bagli' | 'hata';

export interface BagliCihaz {
  cihaz_kimlik: string;
  cihaz_ismi?: string;
  tur?: string;
  baglanti_zamani?: string;
  firmware_surum?: string;
  ip_adres?: string;
  desteklenen_komutlar?: string[];
}

export interface KartOlayVerisi {
  durum: 'koyuldu' | 'cekildi';
  kartKimlik: string;
  cihazKimlik: string;
  zaman: Date;
  kartUptimeSn?: number;
  kaynakDurumCevap?: boolean;
}

export interface ICihazTelemetri {
  cihaz_kimlik: string;
  zaman: string;
  batarya?: { seviye: number; sarj: boolean; destekleniyor: boolean; voltaj_mv?: number };
  ag?: { cevrimici: boolean; tur: string; rssi?: number; ssid?: string; ip?: string; mac?: string };
  ekran?: { genislik: number; yukseklik: number; tamEkran: boolean };
  sistem?: { free_heap?: number; chip_id?: string; cpu_mhz?: number; firmware?: string; user_agent?: string; bellek?: any };
  istatistik?: { toplam_okuma?: number; toplam_hata?: number; son_kart_uid?: string; kart_takili?: boolean };
  karanlikMod?: boolean;
  uptime?: number;
}

export interface ICihazKomutVerisi {
  cihaz_kimlik: string;
  komut_kodu: string;
  parametreler?: Record<string, any>;
  _id?: string;
  gonderen?: string;
}

export interface ICihazKomutCevap {
  komut_id?: string;
  komut_kodu: string;
  cihaz_kimlik: string;
  basarili: boolean;
  mesaj: string;
  durum: string;
  zaman?: number;
}

export interface ICihazOlayVerisi {
  cihaz_kimlik: string;
  cihaz_tipi?: string;
  olay_kodu: string;
  aciklama?: string;
  zaman?: any;
  kaynak?: string;
  veri?: any;
}

@Injectable({ providedIn: 'root' })
export class KartWebSocketService {
  readonly durum$ = new BehaviorSubject<WsBaglantiDurum>('bagli-degil');
  readonly veriGeldi$ = new Subject<string>();
  readonly kartOlayi$ = new Subject<KartOlayVerisi>();
  /** Cihaz filtresinden BAĞIMSIZ ham kart olayları — kart-sahibi widget'ı gibi
   *  kendi UID eşleşmesini yapan dinleyiciler için (cihaz filtresi tuzağını önler) */
  readonly kartOlayiHam$ = new Subject<KartOlayVerisi>();

  /** `pdks_kart_hareketler` koleksiyonunda DEĞİŞİKLİK olduğunda tetiklenir
   *  (kart-sahibi tarafındaki manuel giriş/çıkış ekleme, düzenleme, silme vb.).
   *  Bu değişiklikler doğrudan DB'ye yazıldığı için WS olayı üretmez — firmada-panel
   *  gibi koleksiyonu okuyan bileşenlerin durumu canlı tazeleyebilmesi için
   *  kart-sahibi değişiklik sonrası buraya sinyal yollar. */
  readonly hareketDegisti$ = new Subject<void>();
  readonly hataMesaji$ = new BehaviorSubject<string>('');
  readonly cihazlar$ = new BehaviorSubject<BagliCihaz[]>([]);
  readonly telemetriMap$ = new BehaviorSubject<Map<string, ICihazTelemetri>>(new Map());

  /** Uzak komut geldiginde tetiklenir (HMI tarafinda dinlenir) */
  readonly komutGeldi$ = new Subject<ICihazKomutVerisi>();

  /** Komut cevabi geldiginde tetiklenir (admin tarafinda dinlenir) */
  readonly komutCevabi$ = new Subject<ICihazKomutCevap>();

  /** Cihaz olayi geldiginde tetiklenir (admin tarafinda dinlenir) */
  readonly olayGeldi$ = new Subject<ICihazOlayVerisi>();

  /** Cihaz başına son okunan kart UID'si */
  private _sonKartMap = new Map<string, string>();

  /** Şu an okuyucuda olan kartın UID'si — kart konulduğunda set, çekildinde null */
  readonly aktifKartUid$ = new BehaviorSubject<string | null>(null);

  private _subs: Subscription[] = [];
  private _cihazFiltre = '';
  private _aktif = false;

  private fikoyt = inject(FikoYTService);

  get bagli(): boolean { return this.durum$.value === 'bagli'; }

  get varsayilanUrl(): string {
    return 'FikoYT Sunucu (Socket.IO)';
  }

  baglan(_url?: string, cihazKimlik?: string): Promise<boolean> {
    this.kopar();
    this._cihazFiltre = cihazKimlik || '';
    this._aktif = true;

    const socket = this.fikoyt.io;

    if (!socket) {
      this.hataMesaji$.next('Socket.IO baglantisi bulunamadi');
      this.durum$.next('hata');
      return Promise.resolve(false);
    }

    // Zaten bagli mi?
    if (socket.bagli()) {
      this.dinlemeBasla(socket);
      this.durum$.next('bagli');
      return Promise.resolve(true);
    }

    // Baglanti bekleniyor
    this.durum$.next('baglaniyor');

    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        if (this.durum$.value === 'baglaniyor') {
          this.dinlemeBasla(socket);
          this.hataMesaji$.next('Sunucu henuz bagli degil, yeniden baglaninca otomatik dinlenecek');
          this.durum$.next('bagli');
          resolve(true);
        }
      }, 800);

      const sub = socket.fromEvent('connect').subscribe(() => {
        clearTimeout(timeout);
        if (this._aktif) {
          this.dinlemeBasla(socket);
          this.durum$.next('bagli');
          resolve(true);
        }
        sub.unsubscribe();
      });
      this._subs.push(sub);
    });
  }

  kopar() {
    this._aktif = false;
    for (const sub of this._subs) sub.unsubscribe();
    this._subs = [];
    this.durum$.next('bagli-degil');
    this.aktifKartUid$.next(null);
  }

  /** Socket.IO uzerinden seri komut gonderilemez */
  yaz(_veri: string): boolean {
    return false;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OLAY EMIT (HMI -> Sunucu)
  // ═══════════════════════════════════════════════════════════════════════════

  /** Cihaz olayi logla (sunucuya gonder, DB'ye kaydedilir) */
  olayGonder(olay: Partial<ICihazOlayVerisi>): void {
    const socket = this.fikoyt.io;
    if (!socket) return;
    // cihaz_kimlik yoksa localStorage'dan oku (HMI cihaz kimligi)
    const cihazKimlik = olay.cihaz_kimlik || localStorage.getItem('fikoyt_cihaz_kimlik') || '';
    socket.emit('FikoYT.sistem.io.cihaz.olay', {
      ...olay,
      cihaz_kimlik: cihazKimlik,
      cihaz_tipi: olay.cihaz_tipi || 'hmi_pano',
      zaman: new Date().toISOString(),
    });
  }

  /** Komut cevabi gonder (cihaz -> admin) */
  komutCevabiGonder(cevap: Partial<ICihazKomutCevap>): void {
    const socket = this.fikoyt.io;
    if (!socket) return;
    socket.emit('FikoYT.sistem.io.cihaz.komut-cevap', cevap);
  }

  private dinlemeBasla(socket: any) {
    // Onceki abonelikleri temizle
    for (const sub of this._subs) sub.unsubscribe();
    this._subs = [];

    // Kart okuyucu olayi
    const sub1 = socket.fromEvent('FikoYT.sistem.io.cihaz.guncellendi').subscribe((data: any) => {
      this.olayIsle(data);
    });
    this._subs.push(sub1);

    // Cihaz baglanti bilgisi
    const sub2 = socket.fromEvent('FikoYT.sistem.io.cihaz.baglandi').subscribe((data: any) => {
      this.cihazBaglandiIsle(data);
    });
    this._subs.push(sub2);

    // Durum sorgulama cevabi — sadece cihaz bilgisi için kullan, kart olayı tetikleme
    const sub5 = socket.fromEvent('FikoYT.sistem.io.cihaz.durum-cevap').subscribe((data: any) => {
      this.durumCevabiIsle(data);
    });
    this._subs.push(sub5);

    // Cihaz listesi cevabi (toplu kesfetme)
    const sub7 = socket.fromEvent('FikoYT.sistem.io.cihaz.liste-cevap').subscribe((data: any) => {
      this.cihazListesiIsle(data);
    });
    this._subs.push(sub7);

    // Cihaz telemetri verisi
    const subTelemetri = socket.fromEvent('FikoYT.sistem.io.cihaz.telemetri').subscribe((data: any) => {
      this.telemetriIsle(data);
    });
    this._subs.push(subTelemetri);

    // Cihaz (kart okuyucu) koptu bildirimi
    const sub6 = socket.fromEvent('FikoYT.sistem.io.cihaz.koptu').subscribe((data: any) => {
      this.cihazKoptuIsle(data);
    });
    this._subs.push(sub6);

    // ═══ UZAK KOMUT DINLEYICI (HMI cihaz tarafinda) ═══
    const subKomut = socket.fromEvent('FikoYT.sistem.io.cihaz.komut').subscribe((data: any) => {
      const payload = Array.isArray(data) ? data[0] : data;
      if (payload) {
        this.komutGeldi$.next(payload);
      }
    });
    this._subs.push(subKomut);

    // ═══ KOMUT CEVABI DINLEYICI (Admin tarafinda) ═══
    const subKomutCevap = socket.fromEvent('FikoYT.sistem.io.cihaz.komut-cevap').subscribe((data: any) => {
      const payload = Array.isArray(data) ? data[0] : data;
      if (payload) {
        this.komutCevabi$.next(payload);
      }
    });
    this._subs.push(subKomutCevap);

    // ═══ CIHAZ OLAY DINLEYICI (Admin tarafinda) ═══
    const subOlay = socket.fromEvent('FikoYT.sistem.io.cihaz.olay').subscribe((data: any) => {
      const payload = Array.isArray(data) ? data[0] : data;
      if (payload) {
        this.olayGeldi$.next(payload);
      }
    });
    this._subs.push(subOlay);

    // Disconnect takibi
    const sub3 = socket.fromEvent('disconnect').subscribe(() => {
      if (this._aktif) {
        this.durum$.next('bagli-degil');
      }
    });
    this._subs.push(sub3);

    // Reconnect takibi + durum sorgula
    const sub4 = socket.fromEvent('connect').subscribe(() => {
      if (this._aktif) {
        this.durum$.next('bagli');
        this.durumSorgula(socket);
      }
    });
    this._subs.push(sub4);

    // Ilk baglantida da durum sorgula
    this.durumSorgula(socket);
  }

  /** Sunucudan mevcut kart durumunu ve cihaz listesini sorgula */
  private durumSorgula(socket: any) {
    const sorgu: any = {};
    if (this._cihazFiltre) sorgu.cihaz_kimlik = this._cihazFiltre;
    socket.emit('FikoYT.sistem.io.cihaz.durum-sorgula', sorgu);
    socket.emit('FikoYT.sistem.io.cihaz.liste-sorgula', {});
    socket.emit('FikoYT.sistem.io.cihaz.telemetri-sorgula', sorgu);
    // ESP'ye durum_sorgula komutu gonder — anlik kart_uptime_sn alir
    if (this._cihazFiltre) {
      socket.emit('FikoYT.sistem.io.cihaz.komut', {
        komut_kodu: 'durum_sorgula',
        cihaz_kimlik: this._cihazFiltre,
        _id: 'ds-' + Date.now(),
      });
    }
  }

  /** Cihaz filtresini degistir (yeniden baglanti gerekmez) */
  cihazFiltresiAyarla(cihazKimlik: string) {
    this._cihazFiltre = cihazKimlik || '';
  }

  /** Sunucudan gelen cihaz listesini isle */
  private cihazListesiIsle(data: any) {
    const liste = Array.isArray(data) ? data : (data?.liste || data?.cihazlar || []);
    if (!Array.isArray(liste) || liste.length === 0) return;

    const mevcut = [...this.cihazlar$.value];
    for (const item of liste) {
      if (!item || typeof item !== 'object') continue;
      const kimlik = item.cihaz_kimlik;
      if (!kimlik) continue;
      const idx = mevcut.findIndex(c => c.cihaz_kimlik === kimlik);
      const yeniBilgi: BagliCihaz = {
        cihaz_kimlik: kimlik,
        cihaz_ismi: item.cihaz_ismi || kimlik,
        tur: item.tur || item.cihaz_tipi || 'kart_okuyucu',
        baglanti_zamani: item.baglanti_zamani || new Date().toISOString(),
        firmware_surum: item.firmware_surum,
        ip_adres: item.ip_adres,
        desteklenen_komutlar: item.desteklenen_komutlar,
      };
      if (idx >= 0) {
        mevcut[idx] = { ...mevcut[idx], ...yeniBilgi };
      } else {
        mevcut.push(yeniBilgi);
      }
    }
    this.cihazlar$.next(mevcut);
  }

  private olayIsle(data: any) {
    if (Array.isArray(data)) {
      for (const item of data) this.tekOlayIsle(item);
    } else {
      this.tekOlayIsle(data);
    }
  }

  private tekOlayIsle(payload: any) {
    if (!payload || typeof payload !== 'object') return;

    // ── Kart olayı filtresi ──
    // 'guncellendi' kanalından aktivasyon, telemetri gibi kart-dışı olaylar da geliyor.
    // Sadece gerçek kart olaylarını işle (ESP'den gelen: durum=koyuldu/cekildi)
    const durum = payload.durum ?? '';
    const kartKimlik = payload.kart_kimlik ?? '';

    // tip alanı olan olaylar kart olayı değil (aktivasyon_durum_degisti, telemetri vb.)
    if (payload.tip) return;

    // Kart olayı değilse atla: ne kart_kimlik ne de koyuldu/cekildi durumu var
    if (!kartKimlik && durum !== 'koyuldu' && durum !== 'cekildi') return;

    // Her olaydan cihaz bilgisini cihaz listesine ekle (kesfetme)
    const ck = payload.cihaz_kimlik;
    if (ck) {
      const mevcut = this.cihazlar$.value;
      if (!mevcut.some(c => c.cihaz_kimlik === ck)) {
        this.cihazlar$.next([...mevcut, {
          cihaz_kimlik: ck,
          cihaz_ismi: payload.cihaz_ismi || ck,
          tur: payload.tur || 'kart_okuyucu',
          baglanti_zamani: new Date().toISOString(),
        }]);
      }
    }

    // Cihaz filtresi — yalnızca filtreli akışı (kartOlayi$) etkiler;
    // ham akış (kartOlayiHam$) tüm kart olaylarını taşır
    const filtreUyuyor = !this._cihazFiltre || ck === this._cihazFiltre;

    const now = new Date();

    if (durum === 'cekildi') {
      const cekilenUid = kartKimlik || this._sonKartMap.get(ck || '') || '';
      if (ck) this._sonKartMap.delete(ck);
      if (cekilenUid) {
        const olay: KartOlayVerisi = { durum: 'cekildi', kartKimlik: cekilenUid, cihazKimlik: ck || '', zaman: now };
        this.kartOlayiHam$.next(olay);
        if (filtreUyuyor) this.kartOlayi$.next(olay);
      }
    } else if (kartKimlik && durum === 'koyuldu') {
      if (ck) this._sonKartMap.set(ck, String(kartKimlik));
      const olay: KartOlayVerisi = { durum: 'koyuldu', kartKimlik: String(kartKimlik), cihazKimlik: ck || '', zaman: now, kartUptimeSn: payload.kart_uptime_sn ?? null };
      this.kartOlayiHam$.next(olay);
      if (filtreUyuyor) this.kartOlayi$.next(olay);
    }
    // kartKimlik var ama durum 'koyuldu'/'cekildi' değilse → atla (belirsiz durum)
  }

  /** Durum sorgulama cevabı — cihaz bilgisi güncelle ama kart olayı tetikleme */
  private durumCevabiIsle(data: any) {
    if (Array.isArray(data)) {
      for (const item of data) this.tekDurumCevabiIsle(item);
    } else {
      this.tekDurumCevabiIsle(data);
    }
  }

  private tekDurumCevabiIsle(payload: any) {
    if (!payload || typeof payload !== 'object') return;
    console.log('[DURUM-CEVAP]', JSON.stringify(payload));

    // Cihaz bilgisini listeye ekle (keşfetme amaçlı)
    const ck = payload.cihaz_kimlik;
    if (ck) {
      const mevcut = this.cihazlar$.value;
      if (!mevcut.some(c => c.cihaz_kimlik === ck)) {
        this.cihazlar$.next([...mevcut, {
          cihaz_kimlik: ck,
          cihaz_ismi: payload.cihaz_ismi || ck,
          tur: payload.tur || 'kart_okuyucu',
          baglanti_zamani: new Date().toISOString(),
        }]);
      }
    }

    // durum-cevap'ta kart bilgisi varsa → kart olayı tetikle (mevcut durumu öğren)
    const kartKimlik = payload.kart_kimlik ?? '';
    const durum = payload.durum ?? '';
    if (this._cihazFiltre && ck !== this._cihazFiltre) return;

    if (kartKimlik && durum === 'koyuldu') {
      if (ck) this._sonKartMap.set(ck, String(kartKimlik));
      this.kartOlayi$.next({
        durum: 'koyuldu',
        kartKimlik: String(kartKimlik),
        cihazKimlik: ck || '',
        zaman: new Date(),
        kartUptimeSn: payload.kart_uptime_sn ?? null,
        kaynakDurumCevap: true,
      });
    }
  }

  private cihazBaglandiIsle(data: any) {
    let payload: any;
    if (Array.isArray(data)) {
      payload = data[0];
    } else {
      payload = data;
    }
    if (!payload) return;
    const cihazIsmi = payload?.cihaz_ismi || payload?.cihaz_kimlik || 'Bilinmeyen';
    this.veriGeldi$.next(`[Cihaz Baglandi] ${cihazIsmi}`);

    // Cihaz listesine ekle/guncelle
    const kimlik = payload?.cihaz_kimlik;
    if (kimlik) {
      const mevcut = [...this.cihazlar$.value];
      const idx = mevcut.findIndex(c => c.cihaz_kimlik === kimlik);
      const yeniBilgi: BagliCihaz = {
        cihaz_kimlik: kimlik,
        cihaz_ismi: payload?.cihaz_ismi || '',
        tur: payload?.tur || payload?.cihaz_tipi || 'kart_okuyucu',
        baglanti_zamani: new Date().toISOString(),
        firmware_surum: payload?.firmware_surum,
        ip_adres: payload?.ip_adres,
        desteklenen_komutlar: payload?.desteklenen_komutlar,
      };
      if (idx >= 0) {
        mevcut[idx] = { ...mevcut[idx], ...yeniBilgi };
      } else {
        mevcut.push(yeniBilgi);
      }
      this.cihazlar$.next(mevcut);
    }
  }

  private cihazKoptuIsle(data: any) {
    let payload: any;
    if (Array.isArray(data)) {
      payload = data[0];
    } else {
      payload = data;
    }
    if (!payload) return;

    // Cihaz listesinden cikar
    const kimlik = payload?.cihaz_kimlik;
    if (kimlik) {
      const mevcut = this.cihazlar$.value;
      this.cihazlar$.next(mevcut.filter(c => c.cihaz_kimlik !== kimlik));

      // Telemetri verisini de temizle
      const tMap = new Map(this.telemetriMap$.value);
      tMap.delete(kimlik);
      this.telemetriMap$.next(tMap);
    }

    if (this._cihazFiltre && payload.cihaz_kimlik !== this._cihazFiltre) return;

    const cihazKimlik = payload?.cihaz_kimlik || 'Bilinmeyen';
    this.veriGeldi$.next(`[Cihaz Koptu] ${cihazKimlik}`);
    this.veriGeldi$.next('Kart Okuyucu Baglantisi Kesildi');
  }

  private telemetriIsle(data: any) {
    if (!data || typeof data !== 'object') return;
    const payload = Array.isArray(data) ? data[0] : data;
    if (!payload?.cihaz_kimlik) return;

    const map = new Map(this.telemetriMap$.value);
    map.set(payload.cihaz_kimlik, {
      cihaz_kimlik: payload.cihaz_kimlik,
      zaman: payload.zaman || new Date().toISOString(),
      batarya: payload.batarya,
      ag: payload.ag,
      ekran: payload.ekran,
      sistem: payload.sistem,
      istatistik: payload.istatistik,
      karanlikMod: payload.karanlikMod,
      uptime: payload.uptime,
    });
    this.telemetriMap$.next(map);

    // Cihaz listesinde yoksa ekle
    const mevcut = this.cihazlar$.value;
    if (!mevcut.some(c => c.cihaz_kimlik === payload.cihaz_kimlik)) {
      this.cihazlar$.next([...mevcut, {
        cihaz_kimlik: payload.cihaz_kimlik,
        cihaz_ismi: payload.cihaz_ismi || payload.cihaz_kimlik,
        tur: 'hmi_pano',
        baglanti_zamani: new Date().toISOString(),
      }]);
    }
  }
}
